/**
 * 文字选中快捷菜单组件
 *
 * 当用户在章节或设定中选中文字时，显示快捷菜单
 * 提供"引用"和"复制"两个操作
 */

import React, { useEffect, useCallback, useMemo } from 'react';
import { useTextSelectionStore } from '../../store/textSelectionStore';
import { triggerTextQuote } from '../../utils/textSelectionEvents';
import { calculatePopupPosition } from '../../utils/popupPositioning';

// ==================== SVG 图标 ====================

const QUOTE_ICON = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
  </svg>
);

const COPY_ICON = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const CLOSE_ICON = (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// ==================== 组件定义 ====================

/**
 * TextSelectionPopup 组件
 */
export const TextSelectionPopup: React.FC = React.memo(() => {
  const { isVisible, selectionData, hidePopup } = useTextSelectionStore();

  // 计算弹窗位置
  const popupPosition = useMemo(() => {
    if (!selectionData) return null;

    const { x, y } = calculatePopupPosition(
      selectionData.position.x,
      selectionData.position.y
    );

    return { x, y };
  }, [selectionData]);

  // 处理复制操作
  const handleCopy = useCallback(async () => {
    if (!selectionData) return;

    try {
      // 优先使用现代 clipboard API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(selectionData.text);
      } else {
        // 降级方案：使用传统的 execCommand
        const textArea = document.createElement('textarea');
        textArea.value = selectionData.text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);

        if (!successful) {
          throw new Error('Copy command failed');
        }
      }

      // 复制成功后隐藏弹窗
      hidePopup();
    } catch (err) {
      console.error('[TextSelectionPopup] Copy failed:', err);
      // TODO: 可以在这里显示错误提示
    }
  }, [selectionData, hidePopup]);

  // 处理引用操作
  const handleQuote = useCallback(() => {
    if (!selectionData) return;

    const reference = selectionData.sourceType === 'chapter'
      ? `#{${selectionData.sourceId}}`
      : `@{${selectionData.sourceId}}`;

    triggerTextQuote({
      text: selectionData.text,
      sourceType: selectionData.sourceType,
      sourceId: selectionData.sourceId,
      reference
    });

    hidePopup();
  }, [selectionData, hidePopup]);

  // 监听键盘事件（Esc 关闭弹窗）
  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        hidePopup();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, hidePopup]);

  // 监听点击外部事件
  useEffect(() => {
    if (!isVisible) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('.text-selection-popup')) return;

      // 检查点击是否在选中的文字区域内
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        const clickX = e.clientX;
        const clickY = e.clientY;

        // 如果点击在选中区域内，不关闭弹窗
        if (
          clickX >= rect.left &&
          clickX <= rect.right &&
          clickY >= rect.top &&
          clickY <= rect.bottom
        ) {
          return;
        }
      }

      hidePopup();
    };

    // 延迟添加监听，避免立即触发
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, hidePopup]);

  // 监听滚动事件，滚动时隐藏弹窗
  useEffect(() => {
    if (!isVisible) return;

    const handleScroll = () => {
      hidePopup();
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isVisible, hidePopup]);

  // 监听选中状态变化，如果选中被清除则隐藏弹窗
  useEffect(() => {
    if (!isVisible) return;

    const checkSelection = () => {
      const selection = window.getSelection();
      if (!selection || selection.toString().trim() === '') {
        hidePopup();
      }
    };

    const interval = setInterval(checkSelection, 200);
    return () => clearInterval(interval);
  }, [isVisible, hidePopup]);

  if (!isVisible || !popupPosition) {
    return null;
  }

  return (
    <div
      className="text-selection-popup fixed z-50 bg-white rounded-lg shadow-lg border border-gray-100 flex items-center gap-1.5 px-1.5 py-1 transition-all duration-150 animate-fadeIn"
      style={{
        left: `${popupPosition.x}px`,
        top: `${popupPosition.y}px`
      }}
    >
      {/* 引用按钮 */}
      <button
        onClick={handleQuote}
        className="popup-button-quote flex items-center gap-1 px-2 py-1 text-sm rounded transition-colors bg-ink-800 hover:bg-ink-700 text-white"
        title="引用到聊天框"
      >
        {QUOTE_ICON}
        <span>引用</span>
      </button>

      {/* 复制按钮 */}
      <button
        onClick={handleCopy}
        className="popup-button-copy flex items-center gap-1 px-2 py-1 text-sm rounded transition-colors bg-gray-100 hover:bg-gray-200 text-gray-700"
        title="复制到剪贴板"
      >
        {COPY_ICON}
        <span>复制</span>
      </button>

      {/* 关闭按钮 */}
      <button
        onClick={hidePopup}
        className="p-0.5 text-gray-400 hover:text-gray-600 transition-colors"
        title="关闭 (Esc)"
      >
        {CLOSE_ICON}
      </button>
    </div>
  );
});

TextSelectionPopup.displayName = 'TextSelectionPopup';

export default TextSelectionPopup;
